/**
 * @fileoverview EditResidentModal Component - Resident Information Editor
 * 
 * This component provides a modal dialog for editing resident information in the
 * senior care facility management system. It includes form validation, real-time
 * error handling, and API integration for updating resident records.
 * 
 * Features:
 * - Comprehensive form with personal and health information fields
 * - Real-time form validation with error messages
 * - Responsive design for mobile and desktop
 * - Async submission with loading states
 * - Dropdown selectors for standardized fields (fall risk, mobility, etc.)
 * - Accessible form controls with proper labels
 * 
 * Form Fields:
 * - Personal: First Name*, Last Name*, Age*, MRN
 * - Facility: Room Number*, Mobility Class, Fall Risk Level*, Sleep Quality, Status
 * (* = required fields)
 * 
 * @exports EditResidentModal - Main modal component
 * @exports FormField - Reusable form field wrapper (internal)
 * @exports TextInput - Styled text input component (internal)
 * @exports SelectInput - Styled select dropdown component (internal)
 */

import { useState, useEffect } from "react";
import apiService from "../services/api";

/* ═══════════════════════ CONSTANTS ═══════════════════════ */

/**
 * Color Constants
 * Defines the color palette used throughout the modal component.
 */

/** @constant {string} W - White color for text and borders */
const W = "#ffffff";

/** @constant {string} CARD_BG - Dark blue background for the modal card */
const CARD_BG = "#042558";

/** @constant {string} INPUT_BG - Semi-transparent white background for input fields */
const INPUT_BG = "rgba(255,255,255,0.08)";

/** @constant {string} BORDER - Semi-transparent white border color */
const BORDER = "rgba(255,255,255,0.3)";

/** @constant {string} ACCENT - Blue accent color for headings and highlights */
const ACCENT = "#4A90E2";

/**
 * Form Options Constants
 * Predefined options for dropdown selectors to ensure data consistency.
 */

/** @constant {string[]} FALL_RISK_OPTIONS - Available fall risk levels */
const FALL_RISK_OPTIONS = ["LOW", "MODERATE", "HIGH", "CRITICAL"];

/** @constant {string[]} SLEEP_QUALITY_OPTIONS - Available sleep quality ratings */
const SLEEP_QUALITY_OPTIONS = ["GOOD", "AVERAGE", "POOR"];

/** @constant {string[]} MOBILITY_OPTIONS - Available mobility classifications */
const MOBILITY_OPTIONS = ["INDEPENDENT", "ASSISTED", "WHEELCHAIR"];

/** @constant {string[]} STATUS_OPTIONS - Available resident status values */
const STATUS_OPTIONS = ["ACTIVE", "DISCHARGED", "DECEASED"];

/** @constant {string[]} GENDER_OPTIONS - Available gender options */
const GENDER_OPTIONS = ["M", "F"];

/** @constant {string[]} RISK_FACTOR_OPTIONS - Available risk factors */
const RISK_FACTOR_OPTIONS = [
  "diabetes",
  "neuropathy",
  "polypharmacy",
  "nocturia",
  "cognitive_impairment",
  "vision_impairment",
];

/* ═══════════════════════ FORM FIELD COMPONENTS ═══════════════════════ */

/**
 * FormField - Reusable Form Field Wrapper
 * 
 * Wraps form inputs with a consistent label and layout. Displays a red asterisk
 * for required fields and adapts font sizes based on mobile/desktop viewport.
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Label text to display above the input
 * @param {React.ReactNode} props.children - Input component to render
 * @param {boolean} [props.required=false] - Whether to show required indicator (*)
 * @param {boolean} [props.isMobile=false] - Whether to use mobile-optimized styling
 * @returns {JSX.Element} Form field with label and input
 */
function FormField({ label, children, required, isMobile }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label
        style={{
          fontSize: isMobile ? 12 : 14,
          fontWeight: 600,
          color: W,
          opacity: 0.85,
        }}
      >
        {label} {required && <span style={{ color: "#FF6B6B" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

/**
 * TextInput - Styled Text Input Component
 * 
 * A styled text input field with focus states, consistent styling, and support
 * for different input types (text, number, etc.). Includes hover and focus effects.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current input value
 * @param {Function} props.onChange - Callback function when value changes (receives new value)
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.type="text"] - HTML input type (text, number, email, etc.)
 * @param {Object} props.rest - Additional HTML input attributes
 * @returns {JSX.Element} Styled text input element
 */
function TextInput({ value, onChange, placeholder, type = "text", ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        background: INPUT_BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        fontSize: 14,
        color: W,
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        transition: "border-color 0.2s",
        width: "100%",
        boxSizing: "border-box",
      }}
      onFocus={(e) => (e.target.style.borderColor = ACCENT)}
      onBlur={(e) => (e.target.style.borderColor = BORDER)}
      {...rest}
    />
  );
}

/**
 * SelectInput - Styled Select Dropdown Component
 * 
 * A custom-styled select dropdown with consistent appearance across browsers.
 * Includes a custom dropdown arrow icon and placeholder support.
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Currently selected value
 * @param {Function} props.onChange - Callback function when selection changes (receives new value)
 * @param {string[]} props.options - Array of option values to display
 * @param {string} [props.placeholder] - Placeholder text for empty selection
 * @returns {JSX.Element} Styled select dropdown element
 */
function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: INPUT_BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        fontSize: 14,
        color: value ? W : "rgba(255,255,255,0.4)",
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        cursor: "pointer",
        width: "100%",
        boxSizing: "border-box",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='white' stroke-width='2' fill='none'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 16px center",
      }}
    >
      <option value="" style={{ background: "#0e2240", color: W }}>
        {placeholder || "Select..."}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt} style={{ background: "#0e2240", color: W }}>
          {opt}
        </option>
      ))}
    </select>
  );
}

/**
 * CheckboxGroup - Multi-select Checkbox Group Component
 * 
 * Displays a group of checkboxes as styled buttons that can be toggled on/off.
 * Used for selecting multiple options like risk factors.
 * 
 * @param {Object} props - Component props
 * @param {string[]} props.options - Array of available options
 * @param {string[]} props.selected - Array of currently selected options
 * @param {Function} props.onChange - Callback when selection changes (receives new selected array)
 * @param {boolean} [props.isMobile=false] - Whether to use mobile-optimized styling
 * @returns {JSX.Element} Checkbox group component
 */
function CheckboxGroup({ options, selected, onChange, isMobile }) {
  const toggle = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      {options.map((opt) => {
        const isActive = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            style={{
              background: isActive
                ? "rgba(74,144,226,0.3)"
                : "rgba(255,255,255,0.05)",
              border: `1.5px solid ${
                isActive ? "rgba(74,144,226,0.7)" : BORDER
              }`,
              borderRadius: 8,
              padding: isMobile ? "6px 10px" : "8px 14px",
              fontSize: isMobile ? 11 : 13,
              fontWeight: isActive ? 700 : 400,
              color: W,
              cursor: "pointer",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {isActive && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6ADD00"
                strokeWidth="3"
                style={{ marginRight: 6, verticalAlign: "middle" }}
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
            {opt.replace(/_/g, " ")}
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════ MAIN EDIT MODAL ═══════════════════════ */

/**
 * EditResidentModal - Main Resident Editor Modal Component
 * 
 * A comprehensive modal dialog for editing resident information. Handles form state,
 * validation, API submission, and provides user feedback through loading states and
 * error messages.
 * 
 * Workflow:
 * 1. Initializes form with existing resident data
 * 2. Validates user input on submission
 * 3. Sends update request to API
 * 4. Notifies parent component of successful update
 * 5. Closes modal after successful submission
 * 
 * @param {Object} props - Component props
 * @param {Object} props.resident - Resident object containing current data
 * @param {string} props.resident.resident_id - Unique resident identifier
 * @param {string} props.resident.name - Full name (will be parsed into first/last)
 * @param {number} props.resident.age - Resident's age
 * @param {string} props.resident.room_id - Room number
 * @param {string} [props.resident.mrn] - Medical Record Number
 * @param {string} [props.resident.fall_risk_level] - Fall risk classification
 * @param {string} [props.resident.latest_sleep_quality] - Sleep quality rating
 * @param {string} [props.resident.mobility_class] - Mobility classification
 * @param {string} [props.resident.status] - Resident status
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} [props.onUpdate] - Callback when resident is successfully updated (receives updated resident object)
 * @param {boolean} [props.isMobile=false] - Whether to use mobile-optimized layout
 * @returns {JSX.Element} Modal dialog with edit form
 */



















export default function EditResidentModal({ resident, onClose, onUpdate, isMobile }) {
  
    /* ──────────────────────── Helper Functions ──────────────────────── */
  
  /**
   * Parses a full display name into first and last name components.
   * Handles edge cases like single names or empty strings.
   * 
   * @param {string} displayName - Full name to parse (e.g., "John Smith")
   * @returns {{firstName: string, lastName: string}} Object with parsed name parts
   */
  const parseNameFromDisplay = (displayName) => {
    if (!displayName) return { firstName: "", lastName: "" };
    const nameParts = displayName.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    return { firstName, lastName };
  };
  
    // Parse the resident's name into first and last name components
  const { firstName, lastName } = parseNameFromDisplay(resident.name);

  /* ──────────────────────── Form State Management ──────────────────────── */
  
  /**
   * Form State Variables
   * Each state variable corresponds to a form field and is initialized
   * with the existing resident data.
   */
  
    // Personal Information
  const [formFirstName, setFormFirstName] = useState(firstName || "");
  const [formLastName, setFormLastName] = useState(lastName || "");
  const [dob, setDob] = useState(resident.dob || "");
  const [age, setAge] = useState(resident.age?.toString() || "");
  const [gender, setGender] = useState(resident.gender || "");
  const [heightCm, setHeightCm] = useState(resident.height_cm?.toString() || "");
  const [weightKg, setWeightKg] = useState(resident.weight_kg?.toString() || "");
  const [mrn, setMrn] = useState(resident.mrn || "");
  




                // Facility & Health Information
  const [roomNumber, setRoomNumber] = useState(resident.room_id || "");
  const [unitId, setUnitId] = useState(resident.unit_id || "");
  const [admissionDate, setAdmissionDate] = useState(resident.admission_date || "");
  const [fallRiskLevel, setFallRiskLevel] = useState(resident.fall_risk_level || "");
  const [sleepQuality, setSleepQuality] = useState(resident.latest_sleep_quality || "");
  const [mobilityClass, setMobilityClass] = useState(resident.mobility_class || "");
  const [status, setStatus] = useState(resident.status || "ACTIVE");
  const [riskFactors, setRiskFactors] = useState(resident.risk_factors || []);
  
    // Consent flags
  const [sleepMonitoringConsent, setSleepMonitoringConsent] = useState(resident.sleep_monitoring_consent || false);
  const [videoClipConsent, setVideoClipConsent] = useState(resident.video_clip_consent || false);
  
    // Emergency Contacts
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [editingContactIndex, setEditingContactIndex] = useState(null);

    // Submission State
  /** @type {[boolean, Function]} Whether form is currently being submitted */
  const [submitting, setSubmitting] = useState(false);
  
  /** @type {[string|null, Function]} Error message to display, if any */
    const [error, setError] = useState(null);
  
  /* ──────────────────────── Load Emergency Contacts ──────────────────────── */
  
    useEffect(() => {
      const loadEmergencyContacts = async () => {
        try {
          setLoadingContacts(true);
          const contacts = await apiService.getEmergencyContacts(resident.resident_id);
          const normalizedContacts = (contacts || []).map(contact => ({
            contact_name: contact.contact_name || '',
            relationship: contact.relationship || '',
            phone: contact.phone || '',
            email: contact.email || '',
            contact_priority: contact.contact_priority || 'SECONDARY',
            notify_on_fall: contact.notify_on_fall || false,
            is_legal_guardian: contact.is_legal_guardian || false,
          }));
        
          setEmergencyContacts(normalizedContacts);
        } catch (err) {
          console.error('Failed to load emergency contacts:', err.message);
          setEmergencyContacts([]);
        } finally {
          setLoadingContacts(false);
        }
      };
    
      loadEmergencyContacts();
    }, [resident.resident_id]);
  
  /* ──────────────────────── Emergency Contact Handlers ──────────────────────── */
  
    const addEmergencyContact = () => {
    const newContact = {
      contact_name: '',
      relationship: '',
      phone: '',
      email: '',
      contact_priority: 'SECONDARY',
      notify_on_fall: false,
      is_legal_guardian: false,
    };
    setEmergencyContacts([...emergencyContacts, newContact]);
    // Automatically set the new contact to editing mode
    setEditingContactIndex(emergencyContacts.length);
  };
  
  const removeEmergencyContact = (index) => {
    setEmergencyContacts(emergencyContacts.filter((_, i) => i !== index));
    if (editingContactIndex === index) {
      setEditingContactIndex(null);
    }
  };
  
  const updateEmergencyContact = (index, field, value) => {
    const updated = [...emergencyContacts];
    updated[index] = { ...updated[index], [field]: value };
    setEmergencyContacts(updated);
  };
  
  const startEditingContact = (index) => {
    setEditingContactIndex(index);
  };
  
  const stopEditingContact = () => {
    setEditingContactIndex(null);
  };

  /* ──────────────────────── Form Validation ──────────────────────── */
  
  /**
   * Validates all form fields before submission.
   * Checks for required fields and valid data formats.
   * 
   * @returns {string|null} Error message if validation fails, null if valid
   */
    const validateForm = () => {
    if (!formFirstName.trim()) return "First name is required";
    if (!formLastName.trim()) return "Last name is required";
    if (!age || isNaN(age) || age < 1) return "Valid age is required";
    if (!roomNumber.trim()) return "Room number is required";

    return null;
  };

  /* ──────────────────────── Form Submission ──────────────────────── */
  
  /**
   * Handles form submission.
   * 
   * Process:
   * 1. Validates form data
   * 2. Constructs update payload
   * 3. Sends API request to update resident
   * 4. Notifies parent component of success
   * 5. Closes modal after brief delay
   * 6. Handles and displays any errors
   * 
   * @async
   * @function handleSubmit
   * @returns {Promise<void>}
   */
    const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
            // Construct the update payload with trimmed and formatted values
            const updatedData = {
              first_name: formFirstName.trim(),
              last_name: formLastName.trim(),
              dob: dob || undefined,
              age: parseInt(age),
              gender: gender || undefined,
              height_cm: heightCm ? parseInt(heightCm) : undefined,
              weight_kg: weightKg ? parseFloat(weightKg) : undefined,
                            room_number: roomNumber.trim(),
              bed_id: "",  // Keep empty for backend compatibility
              unit_id: unitId || undefined,
              admission_date: admissionDate || undefined,
              mrn: mrn.trim() || undefined,
              fall_risk_level: fallRiskLevel || undefined,
              latest_sleep_quality: sleepQuality || undefined,
              mobility_class: mobilityClass || undefined,
              status: status,
              risk_factors: riskFactors,
              sleep_monitoring_consent: sleepMonitoringConsent,
              video_clip_consent: videoClipConsent,
            };

            // Send update request to API
            await apiService.updateResident(resident.resident_id, updatedData);

            // Always update emergency contacts to reflect any changes (including deletions)
            await apiService.updateEmergencyContacts(resident.resident_id, emergencyContacts);

            if (onUpdate) {
              const mergedData = {
                ...resident,
                name: `${formFirstName} ${formLastName}`,
                age: parseInt(age),
                room_id: roomNumber,
                mrn: mrn,
                fall_risk_level: fallRiskLevel,
                latest_sleep_quality: sleepQuality,
                mobility_class: mobilityClass,
                status: status,
              };
              onUpdate(mergedData);
            }

            // Close modal after short delay
            setTimeout(() => {
              onClose();
            }, 500);
                } catch (err) {
      console.error('[EditResidentModal] Update failed:', err.message);
      setError(err.message || "Failed to update resident. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

    /* ──────────────────────── Render Configuration ──────────────────────── */
  
  // Determine grid layout based on viewport size
  const gridCols = isMobile ? "1fr" : "1fr 1fr";

  /* ──────────────────────── Component Render ──────────────────────── */
  
  return (
    <>
            {/* Modal Backdrop - Semi-transparent overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          zIndex: 9998,
          animation: "fadeIn 0.2s ease",
        }}
      />

            {/* Modal Container - Main dialog box */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "95%" : "90%",
          maxWidth: 900,
          maxHeight: "90vh",
          background: CARD_BG,
          border: "2px solid #FFFFFF",
          borderRadius: 20,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          zIndex: 9999,
          animation: "slideIn 0.3s ease",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
                {/* ── Modal Header ── */}
        <div
          style={{
            padding: isMobile ? "20px" : "24px 32px",
            borderBottom: `2px solid ${BORDER}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: isMobile ? 20 : 24,
              fontWeight: 700,
              color: W,
            }}
          >
            Edit Resident
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: W,
              cursor: "pointer",
              padding: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 8,
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke={W}
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

                {/* ── Scrollable Content Area ── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: isMobile ? "20px" : "28px 32px",
          }}
        >
                    {/* Error Banner - Displays validation or API errors */}
          {error && (
            <div
              style={{
                background: "rgba(255,107,107,0.12)",
                border: "1.5px solid rgba(255,107,107,0.5)",
                borderRadius: 12,
                padding: "14px 20px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 20,
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FF6B6B"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span style={{ fontSize: 14, color: "#FF6B6B", fontWeight: 500 }}>
                {error}
              </span>
              <button
                onClick={() => setError(null)}
                style={{
                  marginLeft: "auto",
                  background: "transparent",
                  border: "none",
                  color: "#FF6B6B",
                  cursor: "pointer",
                  fontSize: 18,
                }}
              >
                ✕
              </button>
            </div>
          )}

                    {/* ── Form Content ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                        {/* Personal Information Section */}
                        <div>
                          <h3
                            style={{
                              margin: "0 0 16px",
                              fontSize: 16,
                              fontWeight: 700,
                              color: ACCENT,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={ACCENT}
                              strokeWidth="2"
                            >
                              <circle cx="12" cy="8" r="4" />
                              <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
                            </svg>
                            Personal Information
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: gridCols,
                              gap: 16,
                            }}
                          >
                            <FormField label="First Name" required isMobile={isMobile}>
                              <TextInput
                                value={formFirstName}
                                onChange={setFormFirstName}
                                placeholder="e.g. John"
                              />
                            </FormField>
                            <FormField label="Last Name" required isMobile={isMobile}>
                              <TextInput
                                value={formLastName}
                                onChange={setFormLastName}
                                placeholder="e.g. Smith"
                              />
                            </FormField>
                            <FormField label="Date of Birth" isMobile={isMobile}>
                              <TextInput
                                value={dob}
                                onChange={setDob}
                                type="date"
                              />
                            </FormField>
                            <FormField label="Age" required isMobile={isMobile}>
                              <TextInput
                                value={age}
                                onChange={setAge}
                                placeholder="e.g. 78"
                                type="number"
                              />
                            </FormField>
                            <FormField label="Gender" isMobile={isMobile}>
                              <SelectInput
                                value={gender}
                                onChange={setGender}
                                options={GENDER_OPTIONS}
                                placeholder="Select gender"
                              />
                            </FormField>
                            <FormField label="Height (cm)" isMobile={isMobile}>
                              <TextInput
                                value={heightCm}
                                onChange={setHeightCm}
                                placeholder="e.g. 172"
                                type="number"
                              />
                            </FormField>
                            <FormField label="Weight (kg)" isMobile={isMobile}>
                              <TextInput
                                value={weightKg}
                                onChange={setWeightKg}
                                placeholder="e.g. 72.5"
                                type="number"
                                step="0.1"
                              />
                            </FormField>
                            <FormField label="MRN" isMobile={isMobile}>
                              <TextInput
                                value={mrn}
                                onChange={setMrn}
                                placeholder="e.g. MRN-2025-004891"
                              />
                            </FormField>
                          </div>
                        </div>

                        {/* Facility & Health Information Section */}
                        <div>
                          <h3
                            style={{
                              margin: "0 0 16px",
                              fontSize: 16,
                              fontWeight: 700,
                              color: ACCENT,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={ACCENT}
                              strokeWidth="2"
                            >
                              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                              <polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                            Room & Unit Assignment
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: gridCols,
                              gap: 16,
                            }}
                          >
                          <FormField label="Room Number" required isMobile={isMobile}>
                              <TextInput
                                value={roomNumber}
                                onChange={setRoomNumber}
                                placeholder="e.g. 201"
                              />
                            </FormField>
                            <FormField label="Unit" isMobile={isMobile}>
                              <SelectInput
                                value={unitId}
                                onChange={setUnitId}
                                options={[
                                  "UNIT-2-north",
                                  "UNIT-2-south",
                                  "UNIT-3-north",
                                  "UNIT-3-south",
                                ]}
                                placeholder="Select unit"
                              />
                            </FormField>
                            <FormField label="Admission Date" isMobile={isMobile}>
                              <TextInput
                                value={admissionDate}
                                onChange={setAdmissionDate}
                                type="date"
                              />
                            </FormField>
                          </div>
                        </div>

                        {/* Clinical Information Section */}
                        <div>
                          <h3
                            style={{
                              margin: "0 0 16px",
                              fontSize: 16,
                              fontWeight: 700,
                              color: ACCENT,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={ACCENT}
                              strokeWidth="2"
                            >
                              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                            </svg>
                            Clinical Information
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: gridCols,
                              gap: 16,
                            }}
                          >
                            <FormField label="Mobility Class" isMobile={isMobile}>
                              <SelectInput
                                value={mobilityClass}
                                onChange={setMobilityClass}
                                options={MOBILITY_OPTIONS}
                                placeholder="Select mobility"
                              />
                            </FormField>
                            <FormField label="Fall Risk Level" isMobile={isMobile}>
                              <SelectInput
                                value={fallRiskLevel}
                                onChange={setFallRiskLevel}
                                options={FALL_RISK_OPTIONS}
                                placeholder="Select risk level"
                              />
                            </FormField>
                            <FormField label="Sleep Quality" isMobile={isMobile}>
                              <SelectInput
                                value={sleepQuality}
                                onChange={setSleepQuality}
                                options={SLEEP_QUALITY_OPTIONS}
                                placeholder="Select quality"
                              />
                            </FormField>
                            <FormField label="Status" isMobile={isMobile}>
                              <SelectInput
                                value={status}
                                onChange={setStatus}
                                options={STATUS_OPTIONS}
                                placeholder="Select status"
                              />
                            </FormField>
                          </div>

                          {/* Risk Factors */}
                          <div style={{ marginTop: 16 }}>
                            <FormField label="Risk Factors" isMobile={isMobile}>
                              <CheckboxGroup
                                options={RISK_FACTOR_OPTIONS}
                                selected={riskFactors}
                                onChange={setRiskFactors}
                                isMobile={isMobile}
                              />
                            </FormField>
                          </div>
                        </div>

                                                {/* Emergency Contacts Section */}
                        <div>
                          <h3
                            style={{
                              margin: "0 0 16px",
                              fontSize: 16,
                              fontWeight: 700,
                              color: ACCENT,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={ACCENT}
                              strokeWidth="2"
                            >
                              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                            </svg>
                            Emergency Contacts
                          </h3>
                          
                                                    {loadingContacts ? (
                            <div style={{ padding: "20px", textAlign: "center", color: W, opacity: 0.6 }}>
                              Loading contacts...
                            </div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                              {emergencyContacts.length === 0 ? (
                                <div style={{ 
                                  padding: "20px", 
                                  textAlign: "center", 
                                  color: W, 
                                  opacity: 0.6,
                                  background: "rgba(255,255,255,0.03)",
                                  border: `1.5px dashed ${BORDER}`,
                                  borderRadius: 12,
                                }}>
                                  No emergency contacts found. Click "Add Emergency Contact" below to add one.
                                </div>
                              ) : null}
                              
                                                            {emergencyContacts.map((contact, index) => {
                                const isEditing = editingContactIndex === index;
                                
                                return (
                                <div
                                  key={index}
                                  style={{
                                    background: isEditing ? "rgba(74,144,226,0.08)" : "rgba(255,255,255,0.05)",
                                    border: `1.5px solid ${isEditing ? "rgba(74,144,226,0.4)" : BORDER}`,
                                    borderRadius: 12,
                                    padding: isMobile ? "14px" : "16px",
                                    position: "relative",
                                  }}
                                >
                                  {/* Action Buttons */}
                                  <div style={{
                                    position: "absolute",
                                    top: 12,
                                    right: 12,
                                    display: "flex",
                                    gap: 8,
                                  }}>
                                    {isEditing ? (
                                      <button
                                        type="button"
                                        onClick={stopEditingContact}
                                        style={{
                                          background: "rgba(106,221,0,0.2)",
                                          border: "1.5px solid rgba(106,221,0,0.5)",
                                          borderRadius: 8,
                                          padding: "6px 12px",
                                          color: "#6ADD00",
                                          fontSize: 12,
                                          fontWeight: 600,
                                          cursor: "pointer",
                                          transition: "all 0.2s",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = "rgba(106,221,0,0.3)";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = "rgba(106,221,0,0.2)";
                                        }}
                                      >
                                        ✓ Done
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => startEditingContact(index)}
                                        style={{
                                          background: "rgba(74,144,226,0.2)",
                                          border: "1.5px solid rgba(74,144,226,0.5)",
                                          borderRadius: 8,
                                          padding: "6px 12px",
                                          color: ACCENT,
                                          fontSize: 12,
                                          fontWeight: 600,
                                          cursor: "pointer",
                                          transition: "all 0.2s",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = "rgba(74,144,226,0.3)";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = "rgba(74,144,226,0.2)";
                                        }}
                                      >
                                        ✎ Edit
                                      </button>
                                    )}
                                    
                                    <button
                                      type="button"
                                      onClick={() => removeEmergencyContact(index)}
                                      style={{
                                        background: "rgba(255,107,107,0.2)",
                                        border: "1.5px solid rgba(255,107,107,0.5)",
                                        borderRadius: "50%",
                                        width: 28,
                                        height: 28,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "all 0.2s",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "rgba(255,107,107,0.4)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "rgba(255,107,107,0.2)";
                                      }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2.5">
                                        <path d="M18 6L6 18M6 6l12 12"/>
                                      </svg>
                                    </button>
                                  </div>
                                  
                                  {isEditing ? (
                                    // EDIT MODE - Show form fields
                                    <>
                                                                            <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 12, paddingRight: 100 }}>
                                        <FormField label="Name" required isMobile={isMobile}>
                                          <TextInput
                                            value={contact.contact_name || ''}
                                            onChange={(val) => updateEmergencyContact(index, 'contact_name', val)}
                                            placeholder="Contact name"
                                          />
                                        </FormField>
                                        <FormField label="Relationship" isMobile={isMobile}>
                                          <TextInput
                                            value={contact.relationship || ''}
                                            onChange={(val) => updateEmergencyContact(index, 'relationship', val)}
                                            placeholder="e.g. Daughter"
                                          />
                                        </FormField>
                                        <FormField label="Phone" required isMobile={isMobile}>
                                          <TextInput
                                            value={contact.phone || ''}
                                            onChange={(val) => updateEmergencyContact(index, 'phone', val)}
                                            placeholder="Phone number"
                                            type="tel"
                                          />
                                        </FormField>
                                        <FormField label="Email" isMobile={isMobile}>
                                          <TextInput
                                            value={contact.email || ''}
                                            onChange={(val) => updateEmergencyContact(index, 'email', val)}
                                            placeholder="Email address"
                                            type="email"
                                          />
                                        </FormField>
                                        <FormField label="Priority" isMobile={isMobile}>
                                          <SelectInput
                                            value={contact.contact_priority || 'SECONDARY'}
                                            onChange={(val) => updateEmergencyContact(index, 'contact_priority', val)}
                                            options={["PRIMARY", "SECONDARY", "TERTIARY"]}
                                            placeholder="Select priority"
                                          />
                                        </FormField>
                                      </div>
                                      
                                      {/* Checkboxes */}
                                      <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                          <input
                                            type="checkbox"
                                            checked={contact.notify_on_fall || false}
                                            onChange={(e) => updateEmergencyContact(index, 'notify_on_fall', e.target.checked)}
                                            style={{ width: 16, height: 16, cursor: "pointer" }}
                                          />
                                          <span style={{ fontSize: 13, color: W }}>Notify on Fall</span>
                                        </label>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                          <input
                                            type="checkbox"
                                            checked={contact.is_legal_guardian || false}
                                            onChange={(e) => updateEmergencyContact(index, 'is_legal_guardian', e.target.checked)}
                                            style={{ width: 16, height: 16, cursor: "pointer" }}
                                          />
                                          <span style={{ fontSize: 13, color: W }}>Legal Guardian</span>
                                        </label>
                                      </div>
                                    </>
                                  ) : (
                                    // VIEW MODE - Show read-only data
                                    <div style={{ paddingRight: 100 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                                        <div style={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: "50%",
                                          background: "rgba(74,144,226,0.2)",
                                          border: "1.5px solid rgba(74,144,226,0.5)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flexShrink: 0,
                                        }}>
                                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2">
                                            <circle cx="12" cy="8" r="4"/>
                                            <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
                                          </svg>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ fontSize: 16, fontWeight: 700, color: W }}>
                                            {contact.contact_name || "Unnamed Contact"}
                                          </div>
                                          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
                                            {contact.relationship || "No relationship specified"}
                                          </div>
                                        </div>
                                        <div style={{
                                          background: contact.contact_priority === "PRIMARY" ? "rgba(255,107,107,0.2)" : 
                                                     contact.contact_priority === "SECONDARY" ? "rgba(255,165,0,0.2)" : 
                                                     "rgba(74,144,226,0.2)",
                                          border: `1.5px solid ${contact.contact_priority === "PRIMARY" ? "rgba(255,107,107,0.5)" : 
                                                                 contact.contact_priority === "SECONDARY" ? "rgba(255,165,0,0.5)" : 
                                                                 "rgba(74,144,226,0.5)"}`,
                                          borderRadius: 8,
                                          padding: "4px 10px",
                                          fontSize: 11,
                                          fontWeight: 700,
                                          color: W,
                                          textTransform: "uppercase",
                                        }}>
                                          {contact.contact_priority || "N/A"}
                                        </div>
                                      </div>
                                      
                                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                                        <div>
                                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>
                                            Phone
                                          </div>
                                          <div style={{ fontSize: 14, color: W, fontWeight: 500 }}>
                                            {contact.phone || "N/A"}
                                          </div>
                                        </div>
                                        <div>
                                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>
                                            Email
                                          </div>
                                          <div style={{ fontSize: 14, color: W, fontWeight: 500, wordBreak: "break-all" }}>
                                            {contact.email || "N/A"}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {(contact.notify_on_fall || contact.is_legal_guardian) && (
                                        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                                          {contact.notify_on_fall && (
                                            <span style={{
                                              fontSize: 11,
                                              padding: "4px 8px",
                                              background: "rgba(255,107,107,0.2)",
                                              border: "1px solid rgba(255,107,107,0.5)",
                                              borderRadius: 6,
                                              color: W,
                                              fontWeight: 600,
                                            }}>
                                              🚨 Fall Notifications
                                            </span>
                                          )}
                                          {contact.is_legal_guardian && (
                                            <span style={{
                                              fontSize: 11,
                                              padding: "4px 8px",
                                              background: "rgba(74,144,226,0.2)",
                                              border: "1px solid rgba(74,144,226,0.5)",
                                              borderRadius: 6,
                                              color: W,
                                              fontWeight: 600,
                                            }}>
                                              👤 Legal Guardian
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );})}
                              
                              {/* Add Contact Button */}
                              <button
                                type="button"
                                onClick={addEmergencyContact}
                                style={{
                                  background: "rgba(106,221,0,0.15)",
                                  border: "1.5px dashed rgba(106,221,0,0.5)",
                                  borderRadius: 12,
                                  padding: "14px",
                                  color: "#6ADD00",
                                  fontSize: 14,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 8,
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "rgba(106,221,0,0.25)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "rgba(106,221,0,0.15)";
                                }}
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6ADD00" strokeWidth="2.5">
                                  <path d="M12 5v14M5 12h14"/>
                                </svg>
                                Add Emergency Contact
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Consent & Monitoring Section */}
                        <div>
                          <h3
                            style={{
                              margin: "0 0 16px",
                              fontSize: 16,
                              fontWeight: 700,
                              color: ACCENT,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke={ACCENT}
                              strokeWidth="2"
                            >
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                            Consent & Monitoring
                          </h3>
                          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {/* Sleep monitoring consent toggle */}
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                cursor: "pointer",
                                padding: "10px 16px",
                                background: sleepMonitoringConsent
                                  ? "rgba(106,221,0,0.1)"
                                  : INPUT_BG,
                                border: `1.5px solid ${
                                  sleepMonitoringConsent
                                    ? "rgba(106,221,0,0.4)"
                                    : BORDER
                                }`,
                                borderRadius: 10,
                                transition: "all 0.2s",
                              }}
                            >
                              <div
                                onClick={() =>
                                  setSleepMonitoringConsent(!sleepMonitoringConsent)
                                }
                                style={{
                                  width: 44,
                                  height: 24,
                                  borderRadius: 12,
                                  background: sleepMonitoringConsent
                                    ? "#6ADD00"
                                    : "rgba(255,255,255,0.2)",
                                  position: "relative",
                                  transition: "background 0.2s",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                <div
                                  style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "50%",
                                    background: W,
                                    position: "absolute",
                                    top: 3,
                                    left: sleepMonitoringConsent ? 23 : 3,
                                    transition: "left 0.2s",
                                  }}
                                />
                              </div>
                              <span style={{ fontSize: 14, fontWeight: 500, color: W }}>
                                Sleep Monitoring Consent
                              </span>
                            </label>

                            {/* Video clip consent toggle */}
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                cursor: "pointer",
                                padding: "10px 16px",
                                background: videoClipConsent
                                  ? "rgba(106,221,0,0.1)"
                                  : INPUT_BG,
                                border: `1.5px solid ${
                                  videoClipConsent ? "rgba(106,221,0,0.4)" : BORDER
                                }`,
                                borderRadius: 10,
                                transition: "all 0.2s",
                              }}
                            >
                              <div
                                onClick={() => setVideoClipConsent(!videoClipConsent)}
                                style={{
                                  width: 44,
                                  height: 24,
                                  borderRadius: 12,
                                  background: videoClipConsent
                                    ? "#6ADD00"
                                    : "rgba(255,255,255,0.2)",
                                  position: "relative",
                                  transition: "background 0.2s",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                <div
                                  style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "50%",
                                    background: W,
                                    position: "absolute",
                                    top: 3,
                                    left: videoClipConsent ? 23 : 3,
                                    transition: "left 0.2s",
                                  }}
                                />
                              </div>
                              <span style={{ fontSize: 14, fontWeight: 500, color: W }}>
                                Video Clip Consent (Fall Recording)
                              </span>
                            </label>
                          </div>
                        </div>
          </div>
        </div>

                {/* ── Footer Actions ── */}
        <div
          style={{
            padding: isMobile ? "16px 20px" : "20px 32px",
            borderTop: `2px solid ${BORDER}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: `1.5px solid ${BORDER}`,
              borderRadius: 12,
              padding: "12px 28px",
              fontSize: 15,
              fontWeight: 600,
              color: W,
              cursor: submitting ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              opacity: submitting ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!submitting)
                e.currentTarget.style.background = "rgba(255,255,255,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.08)";
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: submitting
                ? "rgba(74,144,226,0.3)"
                : "linear-gradient(135deg, #4A90E2, #357ABD)",
              border: "none",
              borderRadius: 12,
              padding: "12px 32px",
              fontSize: 15,
              fontWeight: 700,
              color: submitting ? "rgba(255,255,255,0.6)" : W,
              cursor: submitting ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: 10,
              boxShadow: submitting
                ? "none"
                : "0 4px 16px rgba(74,144,226,0.3)",
            }}
            onMouseEnter={(e) => {
              if (!submitting)
                e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {submitting ? (
              <>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={W}
                  strokeWidth="2"
                  style={{
                    animation: "spin 1s linear infinite",
                  }}
                >
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                Updating...
              </>
            ) : (
              <>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={W}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>


    </>
  );
}
